import { useEffect, useMemo, useState } from "react";
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, User } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase";
import { ADMIN_UIDS } from "../admin";

function normalizeAnswer(s: string) {
  return s.trim(); // 必要なら全角半角/大小/スペース除去など拡張
}

type CompletionType = "single" | "multiple";
type MultiStepForm = {
  hint: string;
  answer: string;
  file: File | null;
  currentImageUrl: string;
  imagePath: string;
};

const emptyMultiStep = (): MultiStepForm => ({
  hint: "",
  answer: "",
  file: null,
  currentImageUrl: "",
  imagePath: "",
});

export default function AdminUploadPage() {
  const [user, setUser] = useState<User | null>(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = useMemo(() => (user ? ADMIN_UIDS.includes(user.uid) : false), [user]);

  const [id, setId] = useState("");        // Q701 など
  const [title, setTitle] = useState("");
  const [completionType, setCompletionType] = useState<CompletionType>("single");
  const [multiSteps, setMultiSteps] = useState<MultiStepForm[]>([
    emptyMultiStep(),
    emptyMultiStep(),
  ]);
  const [multiStepCountText, setMultiStepCountText] = useState("2");
  const [hint, setHint] = useState("");
  const [answer, setAnswer] = useState("");
  const [answer2, setAnswer2] = useState("");
  const [answer3, setAnswer3] = useState("");
  const [answer4, setAnswer4] = useState("");

  const [answer5, setAnswer5] = useState("");

  const [manualAnswerType, setManualAnswerType] = useState<
    "ひらがな" | "カタカナ" | "漢字" | "アルファベット" | "丸数字"
  >("ひらがな");

  const [manualAnswerLength, setManualAnswerLength] = useState(1);
  const [manualAnswerFormatText, setManualAnswerFormatText] = useState("");
  const [adminMemo, setAdminMemo] = useState("");

  const [level, setLevel] = useState(1);  const [isVisible, setIsVisible] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [exists, setExists] = useState(false);
  const [msg, setMsg] = useState<string>("");
  const [showEmail, setShowEmail] = useState(false);
  const loadExistingPuzzle = async () => {
    setMsg("");
    setCurrentImageUrl("");
    setExists(false);

    if (!id.trim()) {
      setMsg("先にID（例: Q701）を入力してください");
      return;
    }

    try {
      const puzzleRef = doc(db, "puzzles", id.trim());
      const snap = await getDoc(puzzleRef);

      if (!snap.exists()) {
        setMsg("このIDの問題はまだ存在しません。新規作成として登録できます。");
        setTitle("");
        setAdminMemo("");
        setCompletionType("single");
        setMultiSteps([emptyMultiStep(), emptyMultiStep()]);
        setMultiStepCountText("2");
        setHint("");
        setAnswer("");
        setAnswer2("");
        setAnswer3("");
        setAnswer4("");
        setAnswer5("");
        setManualAnswerType("ひらがな");
        setManualAnswerLength(1);
        setLevel(1);
        setIsVisible(true);
        setFile(null);
        return;
      }

      const data = snap.data() as {
        title?: string;
        adminMemo?: string;
        hint?: string;
        answer?: string;
        answerCandidates?: string[];
        imageUrl?: string;
        level?: number;
        isVisible?: boolean;
        completionType?: CompletionType;
        steps?: Array<{
          hint?: string;
          answer?: string;
          imageUrl?: string;
          imagePath?: string;
        }>;
      };

      setExists(true);
      setTitle(data.title ?? "");
      setAdminMemo(data.adminMemo ?? "");
      const loadedSteps = Array.isArray(data.steps) ? data.steps : [];
      const loadedType: CompletionType =
        data.completionType === "multiple" || loadedSteps.length > 1 ? "multiple" : "single";
      setCompletionType(loadedType);
      setMultiSteps(loadedType === "multiple" && loadedSteps.length >= 2
        ? loadedSteps.map((step) => ({
            hint: step.hint ?? "",
            answer: step.answer ?? "",
            file: null,
            currentImageUrl: step.imageUrl ?? "",
            imagePath: step.imagePath ?? "",
          }))
        : [emptyMultiStep(), emptyMultiStep()]
      );
      setMultiStepCountText(String(loadedType === "multiple" ? Math.max(2, loadedSteps.length) : 2));
      setHint(data.hint ?? "");
      setAnswer(data.answer ?? "");
      setAnswer2(data.answerCandidates?.[1] ?? "");
      setAnswer3(data.answerCandidates?.[2] ?? "");
      setAnswer4(data.answerCandidates?.[3] ?? "");
      setAnswer5(data.answerCandidates?.[4] ?? "");
      setLevel(data.level ?? 1);
      setIsVisible(data.isVisible ?? true);
      setCurrentImageUrl(data.imageUrl ?? "");
      setFile(null);
      setMsg("既存の問題を読み込みました。内容を修正して保存できます。");
    } catch (e: any) {
      console.error(e);
      setMsg(`❌ 読み込み失敗: ${e?.message ?? String(e)}`);
    }
  };

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => setUser(u));
  }, []);

  const login = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
    setShowEmail(false);
  };

  const logout = async () => {
    await signOut(auth);
    setShowEmail(false);
  };

  const changeCompletionType = (type: CompletionType) => {
    setCompletionType(type);
    if (type === "multiple") {
      setMultiSteps((current) => current.length >= 2
        ? current
        : [current[0] ?? emptyMultiStep(), emptyMultiStep()]
      );
      setMultiStepCountText((current) => current || "2");
    }
  };

  const setMultiStepCount = (count: number) => {
    const safeCount = Math.min(20, Math.max(2, count || 2));
    setMultiSteps((current) => Array.from(
      { length: safeCount },
      (_, index) => current[index] ?? emptyMultiStep()
    ));
  };

  const changeMultiStepCount = (value: string) => {
    setMultiStepCountText(value);
    if (value === "") return;
    const count = Number(value);
    if (Number.isInteger(count) && count >= 2 && count <= 20) {
      setMultiStepCount(count);
    }
  };

  const normalizeMultiStepCount = () => {
    const count = Number(multiStepCountText);
    const safeCount = Number.isFinite(count) ? Math.min(20, Math.max(2, Math.round(count))) : 2;
    setMultiStepCountText(String(safeCount));
    setMultiStepCount(safeCount);
  };

  const updateMultiStep = (index: number, patch: Partial<MultiStepForm>) => {
    setMultiSteps((current) => current.map(
      (step, stepIndex) => stepIndex === index ? { ...step, ...patch } : step
    ));
  };

  function detectAnswerFormat(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return "";

    const chars = Array.from(trimmed);

    const isCircledNumberOnly = chars.every((char) => /[①-⑳㉑-㉟㊱-㊿]/.test(char));
    if (isCircledNumberOnly) return "丸数字";

    const isNumberOnly = chars.every((char) => /[0-9０-９]/.test(char));
    if (isNumberOnly) return `数字${chars.length}桁`;

    const isHiraganaOnly = chars.every((char) => /[ぁ-ゖー]/.test(char));
    if (isHiraganaOnly) return `ひらがな${chars.length}文字`;

    const isKatakanaOnly = chars.every((char) => /[ァ-ヶー]/.test(char));
    if (isKatakanaOnly) return `カタカナ${chars.length}文字`;

    const isKanjiOnly = chars.every((char) => /[一-龠々〆ヵヶ]/.test(char));
    if (isKanjiOnly) return `漢字${chars.length}文字`;

    const isAlphabetOnly = chars.every((char) => /[A-Za-z]/.test(char));
    if (isAlphabetOnly) return `アルファベット${chars.length}文字`;

    return "";
  }

  const submit = async () => {
    setMsg("");

    if (!user) return setMsg("先にログインしてください");
    if (!isAdmin) return setMsg("管理者ではありません（UIDが一致しません）");
    if (!id.trim()) return setMsg("IDを入力してください");
    if (!title.trim()) return setMsg("タイトルを入力してください");
    const answerCandidates = [answer, answer2, answer3, answer4, answer5]
      .map((item) => normalizeAnswer(item))
      .filter((item, index, self) => item.length > 0 && self.indexOf(item) === index)
      .slice(0, 5);

    const detectedAnswerFormat = detectAnswerFormat(answerCandidates[0] ?? "");

    const selectedManualAnswerFormat =
      manualAnswerType === "丸数字"
        ? "丸数字"
        : `${manualAnswerType}${manualAnswerLength}文字`;

    const typedManualAnswerFormat = manualAnswerFormatText.trim();

    const answerFormat =
    typedManualAnswerFormat ||
    detectedAnswerFormat ||
    selectedManualAnswerFormat;
    if (completionType === "single" && answerCandidates.length === 0) return setMsg("答えを1つ以上入力してください");
    if (completionType === "single" && !file && !currentImageUrl) return setMsg("画像ファイルを選択してください");
    if (completionType === "multiple") {
      const missingIndex = multiSteps.findIndex(
        (step) => !step.answer.trim() || (!step.file && !step.currentImageUrl)
      );
      if (missingIndex >= 0) return setMsg(`問題${missingIndex + 1}の答えと画像を入力してください`);
    }

    setBusy(true);
    try {
      let imageUrl = currentImageUrl;
      let storagePath = "";
      let stepsForSave: Array<{
        hint: string;
        answer: string;
        answerCandidates: string[];
        answerFormat: string;
        imagePath: string;
        imageUrl: string;
      }> = [];

      if (completionType === "multiple") {
        stepsForSave = await Promise.all(multiSteps.map(async (step, index) => {
          let stepImageUrl = step.currentImageUrl;
          let stepImagePath = step.imagePath;
          if (step.file) {
            const ext = step.file.name.split(".").pop() || "png";
            stepImagePath = `riddles/${id.trim()}_${index + 1}.${ext}`;
            const storageRef = ref(storage, stepImagePath);
            await uploadBytes(storageRef, step.file, { contentType: step.file.type });
            stepImageUrl = await getDownloadURL(storageRef);
          }
          const stepAnswer = normalizeAnswer(step.answer);
          return {
            hint: step.hint.trim(),
            answer: stepAnswer,
            answerCandidates: [stepAnswer],
            answerFormat: detectAnswerFormat(stepAnswer),
            imagePath: stepImagePath,
            imageUrl: stepImageUrl,
          };
        }));
        imageUrl = stepsForSave[0].imageUrl;
        storagePath = stepsForSave[0].imagePath;
      } else if (file) {
        const ext = file.name.split(".").pop() || "png";
        storagePath = `riddles/${id.trim()}.${ext}`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, file, { contentType: file.type });
        imageUrl = await getDownloadURL(storageRef);
      }

      // Firestore に保存
      const puzzleRef = doc(db, "puzzles", id.trim());
      await setDoc(
        puzzleRef,
        {
          id: id.trim(),
          title: title.trim(),
          adminMemo,
          hint: hint.trim(),
          answer: answerCandidates[0] ?? "",
          answerCandidates,
          answerFormat,
          imagePath: storagePath || `riddles/${id.trim()}`,
          imageUrl,
          completionType,
          stepCount: completionType === "multiple" ? stepsForSave.length : 1,
          steps: completionType === "multiple" ? stepsForSave : [],
          level,
          isVisible,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          createdBy: user.uid,
        },
        { merge: true }
      );

      setMsg(exists ? "✅ 更新完了しました！" : "✅ 投稿完了しました！");
      setCurrentImageUrl(imageUrl || "");
      if (completionType === "multiple") {
        setMultiSteps(stepsForSave.map((step) => ({
          hint: step.hint,
          answer: step.answer,
          file: null,
          currentImageUrl: step.imageUrl,
          imagePath: step.imagePath,
        })));
      }
      setExists(true);
      setFile(null);
    } catch (e: any) {
      console.error("AdminUploadPage submit error:", e);
      console.error("error code:", e?.code);
      console.error("server response:", e?.serverResponse);
      setMsg(
        `❌ 失敗: ${e?.code ?? "unknown"} ${e?.message ?? String(e)}${
          e?.serverResponse ? ` / ${e.serverResponse}` : ""
        }`
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: 16, fontFamily: "sans-serif" }}>
      <h2>管理者：問題投稿</h2>

      {!user ? (
        <button onClick={login}>Googleでログイン</button>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            ログイン中
            {showEmail && user.email ? `: ${user.email}` : ""}
          </div>
          <button type="button" onClick={() => setShowEmail((prev) => !prev)}>
            {showEmail ? "メアドを隠す" : "メアドを表示"}
          </button>
          <button onClick={logout}>ログアウト</button>
        </div>
      )}

      {user && !isAdmin && (
        <p style={{ color: "crimson" }}>
          このアカウントは管理者ではありません。<br />
          admin.ts の UID を確認してください。
        </p>
      )}

      <hr />

      <label>ID（例: Q701）</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input value={id} onChange={(e) => setId(e.target.value)} style={{ width: "100%", padding: 8 }} />
        <button type="button" onClick={loadExistingPuzzle} style={{ padding: "8px 12px" }}>
          読み込む
        </button>
      </div>

      <div style={{ height: 12 }} />

      <div style={{ marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => {
            setTitle("");
            setAdminMemo("");
            setCompletionType("single");
            setMultiSteps([emptyMultiStep(), emptyMultiStep()]);
            setMultiStepCountText("2");
            setHint("");
            setAnswer("");
            setAnswer2("");
            setAnswer3("");
            setAnswer4("");
            setAnswer5("");
            setManualAnswerType("ひらがな");
            setManualAnswerLength(1);
            setManualAnswerFormatText("");
            setIsVisible(true);
          }}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ccc",
            background: "#fff5f5",
            color: "#c62828",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          入力内容を全てクリア
        </button>
      </div>

      <label>問題の形式</label>
      <div style={{ display: "flex", gap: 8, marginTop: 6, marginBottom: 12 }}>
        {([
          ["single", "1枚で完結"],
          ["multiple", "複数枚で完結"],
        ] as const).map(([type, label]) => {
          const selectedType = completionType === type;
          return (
            <button
              key={type}
              type="button"
              aria-pressed={selectedType}
              onClick={() => changeCompletionType(type)}
              style={{
                flex: 1,
                padding: "11px 12px",
                borderRadius: 8,
                border: selectedType ? "2px solid #173b57" : "1px solid #ccc",
                background: selectedType ? "#173b57" : "white",
                color: selectedType ? "white" : "#173b57",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <label>問題名</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: "100%", padding: 8 }} />

      {completionType === "multiple" && (
        <>
          <div style={{ height: 12 }} />
          <label>セットの問題数（2〜20問）</label>
          <input
            type="number"
            min={2}
            max={20}
            value={multiStepCountText}
            onChange={(e) => changeMultiStepCount(e.target.value)}
            onBlur={normalizeMultiStepCount}
            style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
          />

          {multiSteps.map((step, index) => (
            <fieldset key={index} style={{ marginTop: 16, padding: 14, border: "1px solid #d8e1eb", borderRadius: 10 }}>
              <legend style={{ fontWeight: 700 }}>問題 {index + 1}</legend>
              <label>ヒント（任意）</label>
              <textarea
                value={step.hint}
                onChange={(e) => updateMultiStep(index, { hint: e.target.value })}
                style={{ width: "100%", padding: 8, minHeight: 64, boxSizing: "border-box" }}
              />
              <div style={{ height: 10 }} />
              <label>答え</label>
              <input
                value={step.answer}
                onChange={(e) => updateMultiStep(index, { answer: e.target.value })}
                style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 5 }}>
                解答方法：{detectAnswerFormat(step.answer) || "答えの入力後に自動判定"}
              </div>
              <div style={{ height: 10 }} />
              <label>画像</label>
              {step.currentImageUrl && (
                <img
                  src={step.currentImageUrl}
                  alt={`現在の問題${index + 1}`}
                  style={{ display: "block", width: "100%", maxHeight: 200, objectFit: "contain", margin: "8px 0", borderRadius: 8 }}
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => updateMultiStep(index, { file: e.target.files?.[0] ?? null })}
                style={{ width: "100%" }}
              />
            </fieldset>
          ))}
        </>
      )}

      {completionType === "single" && (
        <>
      <div style={{ height: 12 }} />

      <label>ヒント（任意）</label>
      <textarea value={hint} onChange={(e) => setHint(e.target.value)} style={{ width: "100%", padding: 8, minHeight: 80 }} />

      <div style={{ height: 12 }} />

      <label>答え（最大5個）</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="答え候補1（メイン）"
          style={{ width: "100%", padding: 8 }}
        />
        <input
          value={answer2}
          onChange={(e) => setAnswer2(e.target.value)}
          placeholder="答え候補2"
          style={{ width: "100%", padding: 8 }}
        />
        <input
          value={answer3}
          onChange={(e) => setAnswer3(e.target.value)}
          placeholder="答え候補3"
          style={{ width: "100%", padding: 8 }}
        />
        <input
          value={answer4}
          onChange={(e) => setAnswer4(e.target.value)}
          placeholder="答え候補4"
          style={{ width: "100%", padding: 8 }}
        />
        <input
          value={answer5}
          onChange={(e) => setAnswer5(e.target.value)}
          placeholder="答え候補5"
          style={{ width: "100%", padding: 8 }}
        />
      </div>
      <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
        空欄は無視されます。同じ答えを入れた場合は自動で重複除去されます。
      </div>

      <div style={{ height: 12 }} />

      <label>解答方法</label>
      {(() => {
        const autoAnswerFormat = detectAnswerFormat(
          normalizeAnswer(answer || answer2 || answer3 || answer4 || answer5)
        );

        const selectedManualAnswerFormat =
          manualAnswerType === "丸数字"
            ? "丸数字"
            : `${manualAnswerType}${manualAnswerLength}文字`;
              
        const typedManualAnswerFormat = manualAnswerFormatText.trim();
              
        const manualAnswerFormat =
          typedManualAnswerFormat || selectedManualAnswerFormat;
        return (
          <>
            <div
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d8e1eb",
                background: autoAnswerFormat ? "#f8fbff" : "#fff9e8",
                color: autoAnswerFormat ? "#173b57" : "#735400",
                fontWeight: 700,
                boxSizing: "border-box",
              }}
            >
              {autoAnswerFormat || `手動設定: ${manualAnswerFormat}`}
            </div>

            <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
              答え候補の1番目から自動で判定します。自動判定できない場合のみ、下の手動設定が保存されます。
            </div>


              <div style={{ marginTop: 10 }}>
                <input
                  value={manualAnswerFormatText}
                  onChange={(e) => setManualAnswerFormatText(e.target.value)}
                  placeholder="例：ひらがな5文字 / 記号2文字 / 英数字4文字"
                  style={{
                    width: "100%",
                    padding: 8,
                    borderRadius: 8,
                    border: "1px solid #d8e1eb",
                    boxSizing: "border-box",
                    marginBottom: 8,
                  }}
                />

                <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                  自由入力した内容が優先して保存されます。未入力の場合は下の選択内容が保存されます。
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  {(["ひらがな", "カタカナ", "漢字", "アルファベット", "丸数字"] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setManualAnswerType(type)}
                      style={{
                        padding: "9px 11px",
                        borderRadius: 999,
                        border: manualAnswerType === type ? "2px solid #173b57" : "1px solid #d8e1eb",
                        background: manualAnswerType === type ? "#eef4fb" : "white",
                        color: manualAnswerType === type ? "#173b57" : "#444",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                {manualAnswerType !== "丸数字" && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setManualAnswerLength(num)}
                        style={{
                          minWidth: 40,
                          padding: "9px 10px",
                          borderRadius: 999,
                          border: manualAnswerLength === num ? "2px solid #173b57" : "1px solid #d8e1eb",
                          background: manualAnswerLength === num ? "#eef4fb" : "white",
                          color: manualAnswerLength === num ? "#173b57" : "#444",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                )}
              </div>

          </>
        );
      })()}
        </>
      )}

      <div style={{ height: 12 }} />

      {isAdmin && (
        <>
          <label htmlFor="admin-memo">メモ（管理者用）</label>
          <textarea
            id="admin-memo"
            value={adminMemo}
            onChange={(e) => setAdminMemo(e.target.value)}
            placeholder="問題に関するメモを入力"
            style={{ width: "100%", padding: 8, minHeight: 100, boxSizing: "border-box", resize: "vertical" }}
          />
          <div style={{ height: 12 }} />
        </>
      )}

      <label>難易度</label>
      <select value={level} onChange={(e) => setLevel(Number(e.target.value))} style={{ width: "100%", padding: 8 }}>
        <option value={1}>難易度1</option>
        <option value={2}>難易度2</option>
        <option value={3}>難易度3</option>
        <option value={4}>難易度4</option>
        <option value={5}>難易度5</option>
      </select>

      <div style={{ height: 12 }} />

      <label>表示設定</label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setIsVisible(true)}
          style={{
            flex: 1,
            padding: "10px 12px",
            borderRadius: 8,
            border: isVisible ? "2px solid #173b57" : "1px solid #ccc",
            background: isVisible ? "#eef4fb" : "white",
            color: "#173b57",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          表示
        </button>
        <button
          type="button"
          onClick={() => setIsVisible(false)}
          style={{
            flex: 1,
            padding: "10px 12px",
            borderRadius: 8,
            border: !isVisible ? "2px solid #c62828" : "1px solid #ccc",
            background: !isVisible ? "#fff5f5" : "white",
            color: !isVisible ? "#c62828" : "#444",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          非表示
        </button>
      </div>

      <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
        非表示にした問題はサイトの一覧に出なくなります。
      </div>

      <div style={{ height: 12 }} />

      {completionType === "single" && (
        <>
          <label>画像（新しい画像を選ぶと差し替え）</label>
          {currentImageUrl && (
            <div style={{ marginBottom: 8 }}>
              <img
                src={currentImageUrl}
                alt="current puzzle"
                style={{ width: "100%", maxHeight: 220, objectFit: "contain", border: "1px solid #ddd", borderRadius: 8 }}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                現在登録されている画像
              </div>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            style={{ width: "100%" }}
          />
        </>
      )}

      <div style={{ height: 16 }} />

      <button onClick={submit} disabled={busy || !user || !isAdmin} style={{ width: "100%", padding: 12 }}>
        {busy ? "アップロード中…" : exists ? "更新する" : "投稿する"}
      </button>

      {msg && <p style={{ marginTop: 12 }}>{msg}</p>}
    </div>
  );
}
